import React, { useEffect } from 'react';
import type { Vehicle } from '../../types/vinvrmsearch.types';
import type { VehicleSearchErrorKind } from '../../utils/vehicleSearchError';
import { VEHICLE_SEARCH_ERROR_COPY } from '../../utils/vehicleSearchError';
import { VehicleCard } from './VehicleCard';
import { Alert, Badge, LoadingSpinner } from '@shared/components';

interface VehicleResultsProps {
    isLoading: boolean;
    vehicles: Vehicle[];
    onSelect?: (vehicle: Vehicle) => void;
    searchError?: VehicleSearchErrorKind | null;
    showMotHistory?: boolean;
}

export const VehicleResults: React.FC<VehicleResultsProps> = ({
    isLoading = false,
    vehicles,
    onSelect,
    searchError,
    showMotHistory = false,
}) => {
    const [isFirst, setIsFirst] = React.useState<boolean>(true);

    useEffect(() => {
        // Check if this is the first time the component is rendered
        if (isFirst) setIsFirst(!isLoading && vehicles.length === 0);
    }, [isFirst, isLoading, vehicles]);

    return (
        <div className="relative rounded-lg border border-mp-border bg-mp-surface p-6 space-y-6 h-full">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-mp-foreground">Found Vehicles</h3>
                <Badge variant="outline">
                    {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} found
                </Badge>
            </div>

            {isFirst ? (
                <Alert variant="info" role="note">
                    <div className="flex flex-col items-start gap-2">
                        <p className="font-semibold">Vehicle Search</p>
                        <p>Please choose a method to search for vehicles.</p>
                    </div>
                </Alert>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3 md:gap-5">
                    {isLoading && vehicles.length === 0 && (
                        <LoadingSpinner className="absolute left-0 right-0 top-0 bottom-0 p-4" />
                    )}
                    {!isLoading && vehicles.length === 0 && searchError === 'unavailable' && (
                        <div className="md:col-span-2 2xl:col-span-3">
                            <Alert variant="error">
                                <div className="flex flex-col items-start gap-2">
                                    <p className="font-semibold">Vehicle Search Unavailable</p>
                                    <p>{VEHICLE_SEARCH_ERROR_COPY.unavailable}</p>
                                </div>
                            </Alert>
                        </div>
                    )}
                    {!isLoading && vehicles.length === 0 && searchError !== 'unavailable' && (
                        <div className="md:col-span-2 2xl:col-span-3">
                            <Alert variant="warning" role="status">
                                <div className="flex flex-col items-start gap-2">
                                    <p className="font-semibold">No Vehicles Found</p>
                                    <p>
                                        Unfortunately, there are no vehicles available at the moment from
                                        your VIN or VRN vehicle search.
                                    </p>
                                </div>
                            </Alert>
                        </div>
                    )}
                    {vehicles.map((vehicle, index) => (
                        <VehicleCard
                            key={
                                (vehicle as Vehicle).vin ??
                                (vehicle as Vehicle).id ??
                                (vehicle as Vehicle).derivative?.short_name ??
                                `vehicle-${index}`
                            }
                            vehicle={vehicle as Vehicle}
                            onSelect={onSelect}
                            showMotHistory={showMotHistory}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
